import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, User, Lock, LogIn, Sparkles, Shield, ChevronRight, ChevronLeft, Mail, Check, AlertCircle, Cpu, Zap, Globe, Database, Lock as LockIcon, Wifi, Smartphone, Monitor, Server, Code, Layers, Target, Rocket, Star, Hexagon, Triangle, Circle, Square, Diamond, BarChart3, PieChart, LineChart, TrendingUp, Activity, Brain, FileText, Search, Filter, BarChart, PieChart as PieChartIcon, LineChart as LineChartIcon, TrendingDown, Users, Settings, Network, Clock, BarChart2, Settings2 } from 'lucide-react';
import '../styles/login-v2.css';
import '../styles/password-reset.css';

const LoginV2 = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  // Estados para forgot password
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  // Mouse tracking para efectos parallax
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      setMousePosition({ x, y });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { success, role } = await login(username, password);
      if (success) {
        navigate(role === 'admin' ? '/admin' : '/');
      } else {
        setError('Credenciales inválidas');
      }
    } catch (err) {
      setError('Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: forgotEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        setForgotSuccess(true);
      } else {
        setForgotError(data.detail || 'Error al procesar la solicitud');
      }
    } catch (err) {
      setForgotError('Error de conexión. Por favor, intenta nuevamente.');
    } finally {
      setForgotLoading(false);
    }
  };

  // Animaciones
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 10
      }
    }
  };

  const floatingElements = [
    // Primera fila - gráficos y análisis
    { icon: <BarChart3 className="w-6 h-6" />, delay: 0, position: { left: '10%', top: '5%' } },
    { icon: <PieChart className="w-5 h-5" />, delay: 0.5, position: { left: '25%', top: '8%' } },
    { icon: <LineChart className="w-5 h-5" />, delay: 1, position: { left: '40%', top: '6%' } },
    { icon: <TrendingUp className="w-6 h-6" />, delay: 1.5, position: { left: '55%', top: '9%' } },
    { icon: <Activity className="w-5 h-5" />, delay: 2, position: { left: '70%', top: '7%' } },
    { icon: <Brain className="w-6 h-6" />, delay: 2.5, position: { left: '85%', top: '5%' } },
    
    // Segunda fila - datos e información
    { icon: <Database className="w-4 h-4" />, delay: 3, position: { left: '15%', top: '25%' } },
    { icon: <FileText className="w-5 h-5" />, delay: 3.5, position: { left: '30%', top: '28%' } },
    { icon: <Search className="w-6 h-6" />, delay: 4, position: { left: '45%', top: '26%' } },
    { icon: <Filter className="w-5 h-5" />, delay: 4.5, position: { left: '60%', top: '29%' } },
    { icon: <Settings className="w-4 h-4" />, delay: 5, position: { left: '75%', top: '27%' } },
    
    // Tercera fila - métricas y estadísticas
    { icon: <BarChart className="w-4 h-4" />, delay: 5.5, position: { left: '20%', top: '45%' } },
    { icon: <PieChartIcon className="w-5 h-5" />, delay: 6, position: { left: '35%', top: '48%' } },
    { icon: <LineChartIcon className="w-4 h-4" />, delay: 6.5, position: { left: '50%', top: '46%' } },
    { icon: <TrendingDown className="w-5 h-5" />, delay: 7, position: { left: '65%', top: '49%' } },
    { icon: <Users className="w-4 h-4" />, delay: 7.5, position: { left: '80%', top: '47%' } },
    
    // Cuarta fila - elementos adicionales de análisis
    { icon: <Network className="w-4 h-4" />, delay: 8, position: { left: '12%', top: '65%' } },
    { icon: <Target className="w-5 h-5" />, delay: 8.5, position: { left: '28%', top: '68%' } },
    { icon: <Circle className="w-4 h-4" />, delay: 9, position: { left: '42%', top: '66%' } },
    { icon: <Square className="w-5 h-5" />, delay: 9.5, position: { left: '58%', top: '69%' } },
    { icon: <Diamond className="w-4 h-4" />, delay: 10, position: { left: '72%', top: '67%' } },
    
    // Elementos adicionales dispersos
    { icon: <Hexagon className="w-4 h-4" />, delay: 10.5, position: { left: '8%', top: '35%' } },
    { icon: <Triangle className="w-3 h-3" />, delay: 11, position: { left: '88%', top: '35%' } },
    { icon: <Star className="w-3 h-3" />, delay: 11.5, position: { left: '5%', top: '55%' } },
    { icon: <Globe className="w-4 h-4" />, delay: 12, position: { left: '92%', top: '55%' } },
    { icon: <Server className="w-3 h-3" />, delay: 12.5, position: { left: '18%', top: '15%' } },
    { icon: <Cpu className="w-4 h-4" />, delay: 13, position: { left: '82%', top: '15%' } },
  ];

  return (
    <div className="login-container-v2" ref={containerRef}>
      {/* Background Elements */}
      <div className="background-wrapper">
        {/* Floating Waves */}
        <div className="floating-waves">
          <div className="floating-wave wave-1" />
          <div className="floating-wave wave-2" />
          <div className="floating-wave wave-3" />
        </div>
        
        {/* Animated Gradient Orbs */}
        <div className="gradient-orb orb-1" />
        <div className="gradient-orb orb-2" />
        <div className="gradient-orb orb-3" />
        <div className="gradient-orb orb-4" />
        <div className="gradient-orb orb-5" />
        <div className="gradient-orb orb-6" />
        
        {/* Grid Pattern */}
        <div className="grid-pattern" />
        
        {/* Floating Elements */}
        <div className="floating-elements">
          {floatingElements.map((element, index) => (
            <motion.div
              key={index}
              className="floating-element"
              initial={{ opacity: 0, scale: 0, rotate: 0 }}
              animate={{ 
                opacity: [0.3, 0.8, 0.3],
                scale: [0.8, 1.2, 0.8],
                rotate: [0, 360],
                x: [0, Math.random() * 40 - 20, 0],
                y: [0, Math.random() * 40 - 20, 0],
              }}
              transition={{
                duration: 8 + Math.random() * 4,
                delay: element.delay,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }}
              style={{
                left: element.position.left,
                top: element.position.top,
              }}
            >
              {element.icon}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <motion.div
        className="login-content"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Left Side - Branding */}
        <motion.div 
          className="branding-section"
          style={{
            transform: `translateX(${mousePosition.x * 10}px) translateY(${mousePosition.y * 10}px)`
          }}
        >
          <motion.div className="logo-container" variants={itemVariants}>
            <div className="logo-wrapper">
              <img src={`/AMBTU_logo.svg`} alt="AmbTu" className="logo" />
              <motion.div 
                className="logo-glow"
                animate={{
                  scale: [1, 1.02, 1],
                  opacity: [0.15, 0.25, 0.15]
                }}
                transition={{
                  duration: 6,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
            </div>
          </motion.div>

          <motion.h1 
            className="brand-title" 
            variants={itemVariants}
            whileHover={{ 
              scale: 1.05,
              transition: { duration: 0.3, ease: "easeInOut" }
            }}
          >
            <motion.span 
              className="gradient-text"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ 
                duration: 1.2,
                delay: 0.5,
                ease: "easeOut"
              }}
            >
              AmbTu Platform
            </motion.span>
          </motion.h1>

          <motion.p 
            className="brand-subtitle" 
            variants={itemVariants}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ 
              duration: 1,
              delay: 1.2,
              ease: "easeInOut"
            }}
          >
            Sistema avanzado de análisis y gestión de campañas
          </motion.p>

          {/* Feature Cards */}
          <motion.div className="feature-cards" variants={itemVariants}>
            <motion.div 
              className="feature-card"
              whileHover={{ scale: 1.02, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <Clock className="feature-icon" />
              <span>Monitoreo en Tiempo Real</span>
            </motion.div>
            <motion.div 
              className="feature-card"
              whileHover={{ scale: 1.02, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <BarChart2 className="feature-icon" />
              <span>Análisis Avanzado de Resultados</span>
            </motion.div>
            <motion.div 
              className="feature-card"
              whileHover={{ scale: 1.02, y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <Settings2 className="feature-icon" />
              <span>Gestión Centralizada de Campañas</span>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Right Side - Login Form */}
        <motion.div 
          className="form-section login-form-section"
          variants={itemVariants}
          style={{
            transform: `translateX(${mousePosition.x * -5}px) translateY(${mousePosition.y * -5}px)`
          }}
        >
          <div className="glass-card">
            {!showForgotPassword ? (
              <>
                <motion.div className="card-header" variants={itemVariants}>
                  <motion.h2 
                    className="form-title"
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ 
                      duration: 0.8,
                      delay: 0.7,
                      ease: "easeOut"
                    }}
                    whileHover={{
                      scale: 1.02,
                      transition: { duration: 0.2 }
                    }}
                  >
                    Portal de Acceso
                  </motion.h2>
                  <motion.p 
                    className="form-subtitle"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ 
                      duration: 0.6,
                      delay: 0.9,
                      ease: "easeOut"
                    }}
                  >
                    Introduce tus credenciales para acceder a la plataforma.
                  </motion.p>
                </motion.div>

                <form onSubmit={handleSubmit} className="login-form">
                  <motion.div 
                    className={`form-group ${focusedField === 'username' ? 'focused' : ''}`}
                    variants={itemVariants}
                  >
                    <div className="input-wrapper">
                      <User className="input-icon" />
                      <input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        onFocus={() => setFocusedField('username')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Usuario"
                        required
                        className="form-input"
                      />
                      <motion.div 
                        className="input-glow"
                        animate={{
                          opacity: focusedField === 'username' ? 0.3 : 0
                        }}
                      />
                    </div>
                  </motion.div>

                  <motion.div 
                    className={`form-group ${focusedField === 'password' ? 'focused' : ''}`}
                    variants={itemVariants}
                  >
                    <div className="input-wrapper">
                      <Lock className="input-icon" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        onFocus={() => setFocusedField('password')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="Contraseña"
                        required
                        className="form-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="toggle-password"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                      <motion.div 
                        className="input-glow"
                        animate={{
                          opacity: focusedField === 'password' ? 0.3 : 0
                        }}
                      />
                    </div>
                  </motion.div>

                  <AnimatePresence>
                    {error && (
                      <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    type="submit"
                    className="submit-button"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={loading}
                  >
                    <span className="button-content">
                      {loading ? (
                        <motion.div
                          className="loading-spinner"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <>
                          <span>Entrar</span>
                          <ChevronRight className="button-icon" />
                        </>
                      )}
                    </span>
                    <div className="button-glow" />
                  </motion.button>

                  <motion.div className="form-footer" variants={itemVariants}>
                    <motion.button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="forgot-password"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.2 }}
                    >
                      ¿Olvidaste tu contraseña?
                    </motion.button>
                  </motion.div>
                </form>
              </>
            ) : !forgotSuccess ? (
              <>
                <motion.div className="card-header" variants={itemVariants}>
                  <motion.h2 className="form-title">
                    ¿Olvidaste tu contraseña?
                  </motion.h2>
                  <motion.p className="form-subtitle">
                    Introduce tu dirección de correo electrónico y te enviaremos un enlace para restablecer tu contraseña.
                  </motion.p>
                </motion.div>

                <form onSubmit={handleForgotPasswordSubmit} className="login-form">
                  <motion.div 
                    className={`form-group ${focusedField === 'forgotEmail' ? 'focused' : ''}`}
                    variants={itemVariants}
                  >
                    <div className="input-wrapper">
                      <Mail className="input-icon" />
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        onFocus={() => setFocusedField('forgotEmail')}
                        onBlur={() => setFocusedField(null)}
                        placeholder="correo@ejemplo.com"
                        required
                        className="form-input"
                      />
                      <motion.div 
                        className="input-glow"
                        animate={{
                          opacity: focusedField === 'forgotEmail' ? 0.3 : 0
                        }}
                      />
                    </div>
                  </motion.div>

                  <AnimatePresence>
                    {forgotError && (
                      <motion.div
                        className="error-message"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                      >
                        <AlertCircle size={16} />
                        <span>{forgotError}</span>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button
                    type="submit"
                    className="submit-button"
                    variants={itemVariants}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    disabled={forgotLoading}
                  >
                    <span className="button-content">
                      {forgotLoading ? (
                        <motion.div
                          className="loading-spinner"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                      ) : (
                        <>
                          <span>Enviar Enlace de Recuperación</span>
                          <ChevronRight className="button-icon" />
                        </>
                      )}
                    </span>
                    <div className="button-glow" />
                  </motion.button>

                  <motion.div className="form-footer" variants={itemVariants}>
                    <motion.button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(false);
                        setForgotEmail('');
                        setForgotError('');
                      }}
                      className="back-to-login"
                      whileHover={{ x: -5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <ChevronLeft size={16} />
                      <span>Volver al inicio de sesión</span>
                    </motion.button>
                  </motion.div>
                </form>
              </>
            ) : (
              <div className="success-container">
                <div className="success-icon-wrapper">
                  <div className="success-icon">
                    <Check size={48} />
                  </div>
                </div>

                <h2 className="success-title">
                  ¡Correo Enviado!
                </h2>

                <p className="success-message">
                  Si el correo electrónico existe en nuestro sistema, recibirás un enlace para restablecer tu contraseña.
                </p>

                <p className="success-submessage">
                  Por favor, revisa tu bandeja de entrada y tu carpeta de spam. El enlace expirará en 1 hora.
                </p>

                <motion.button
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotSuccess(false);
                    setForgotEmail('');
                  }}
                  className="submit-button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="button-content">
                    <ChevronLeft className="button-icon" />
                    <span>Volver al Inicio de Sesión</span>
                  </span>
                  <div className="button-glow" />
                </motion.button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>

      {/* Bottom Wave */}
      <div className="bottom-wave">
        <div className="bottom-wave-additional"></div>
        <svg viewBox="0 0 1440 320" preserveAspectRatio="none">
          <path 
            fill="url(#wave-gradient)" 
            fillOpacity="1" 
            d="M0,96L48,112C96,128,192,160,288,186.7C384,213,480,235,576,213.3C672,192,768,128,864,128C960,128,1056,192,1152,213.3C1248,235,1344,213,1392,202.7L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
          <defs>
            <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#8b4513" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#a0522d" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#8b4513" stopOpacity="0.15" />
            </linearGradient>
          </defs>
        </svg>
      </div>
    </div>
  );
};

export default LoginV2;
